import React, { useRef } from 'react'

import { useConfigurationStore, useFreeSpinsBonusesStore } from '@stores'

import spritesheetSrc from '@assets/bonus-system/gift-animation.png'
import animatedSpriteConfig from '@assets/bonus-system/gift-animation.json'
import { useFormatMoney } from '@hooks'
import { AnimatedSprite, useShrinkTextToFit } from '@amusnet/react-ui'
import { useTranslation } from '@amusnet/translations/react'

const FSEndBonus: React.FC = () => {
    const totalWin = useFreeSpinsBonusesStore((state) => state.totalWin)
    const hasActiveBonusesLeft = useFreeSpinsBonusesStore((state) => state.hasActiveBonusesLeft)

    const { t } = useTranslation()
    const formatMoney = useFormatMoney()
    const currency = useConfigurationStore((state) => state.currency)

    const formattedTotalWin = formatMoney(Number(totalWin))
    const totalWinAmountRef = useRef<HTMLParagraphElement>(null)

    const text = hasActiveBonusesLeft
        ? t('gameui.bonusSystem.stillHaveActiveBonuses')
        : t('gameui.bonusSystem.playingWithRealMoney')
    const bottomTextRef = useRef<HTMLParagraphElement>(null)

    if (totalWin === '0') {
        return (
            <div id="fs-end-bonus" className="flex flex-col items-center justify-between">
                <div id="fs-end-bonus-content" className="flex flex-col w-full bg-[#19191C]">
                    <AnimatedSprite
                        spritesheetSrc={spritesheetSrc}
                        config={animatedSpriteConfig}
                        duration={1.5}
                        className="m-auto"
                    />
                    <div
                        id="end-bonus-text"
                        className="flex flex-col items-center w-full h-[273px] bg-[linear-gradient(180deg,#19191C_19.44%,#793F06_100%)] text-center"
                    >
                        <p id="fs-end-bonus-no-win" className="text-4xl font-bold text-white">
                            {t('gameui.bonusSystem.youDidNotScoreAWin') ?? "You didn't score a win."}
                        </p>
                        <p id="fs-end-bonus-better-luck" className="text-4xl font-black text-yellow-300 leading-tight">
                            {t('gameui.bonusSystem.betterLuckNextTime') ?? 'BETTER LUCK NEXT TIME!'}
                        </p>
                        <p id="fs-end-bonus-info" className="text-4xl font-bold text-white pt-14">
                            {text}
                        </p>
                    </div>
                </div>
            </div>
        )
    }

    useShrinkTextToFit({
        refresh: [formattedTotalWin],
        designFontSize: 128,
        bounds: { width: 590 },
        textRef: totalWinAmountRef,
    })

    useShrinkTextToFit({
        refresh: [text],
        designFontSize: 36,
        bounds: { width: 720 },
        textRef: bottomTextRef,
    })

    return (
        <div id="fs-end-bonus" className="flex flex-col items-center justify-between">
            <div id="fs-end-bonus-content" className="flex flex-col w-full bg-[#19191C]">
                <AnimatedSprite
                    spritesheetSrc={spritesheetSrc}
                    config={animatedSpriteConfig}
                    duration={1.5}
                    className="m-auto"
                />
                <div
                    id="end-bonus-text"
                    className="flex flex-col items-center w-full h-[273px] bg-[linear-gradient(180deg,#19191C_19.44%,#793F06_100%)]"
                >
                    <p id="fs-end-bonus-ended" className="text-4xl font-bold text-white">
                        {t('gameui.bonusSystem.hasEnded')}
                    </p>
                    <p id="fs-end-bonus-won" className="text-4xl font-black text-yellow-300 leading-tight">
                        {t('gameui.bonusSystem.youHaveWon')}
                    </p>

                    <div id="fs-end-bonus-win-amount-container" className="flex flex-col items-center leading-tight">
                        <p
                            id="fs-end-bonus-win-amount"
                            className="text-9xl font-black text-yellow-300"
                            ref={totalWinAmountRef}
                        >
                            {formattedTotalWin}
                            <span id="fs-end-bonus-win-currency" className="text-5xl font-black text-yellow-300 pl-2">
                                {currency}
                            </span>
                        </p>
                    </div>

                    <p id="fs-end-bonus-info" className="text-4xl font-bold text-white" ref={bottomTextRef}>
                        {text}
                    </p>
                </div>
            </div>
        </div>
    )
}

export default FSEndBonus
